import { BlurbShape } from 'components/city-blocks/blurb';
import { ContentContainer } from 'components/city-blocks/content-container';
import { Quote } from 'components/city-blocks/quote';
import Link from 'next/link';
import { getAssetPath } from 'utils/asset-utils';
import { FontSize } from 'web-common/src/shared/styles/font';

export const META_CONTENT = `Select from a variety of homemade dishes and cuisines for delivery 
in your area from local Shefs! Looking for traditional homecooked meals delivered? Order today!`;
export const INDEX_TITLE = 'Find Home Cooked Food Delivery Near Me!';
export const HEADER_COPY_TITLE_1 = 'Where Shef Cooks & Delivers Homemade Food!';
export const HEADER_COPY_CONTENT_1 = (
  <ContentContainer fontSize={FontSize.M}>
    <Quote
      author='Winona LaDuke'
      text='Food for us comes from our relatives, whether they have wings or fins or roots.
      That is how we consider food. Food has a culture. It has a history. It has a story. It has relationships.'
    />
    Shef is a chef-to-consumer marketplace that connects talented local cooks with customers looking for healthy,
    homemade meals. Shef is a great alternative to fast food or takeout, as it provides a healthier, more personalized
    food delivery experience. Plus, with the ability to filter by dietary preferences and ingredients, Shef makes it
    easy for customers to find dishes that fit their specific needs and preferences. Whether you're looking for a quick
    lunchtime meal or a hearty dinner for the whole family, Shef has something for everyone.
  </ContentContainer>
);

export const HEADER_COPY_TITLE_2 = 'Most Popular Homemade Food in Locations';
export const HEADER_COPY_CONTENT_2 = (
  <ContentContainer fontSize={FontSize.M}>
    <div className='flex w-full flex-col items-center justify-between gap-3 md:flex-row md:gap-6'>
      <div className='w-full md:w-1/2'>
        <img
          className='w-full'
          src={getAssetPath('/images/CDN/Packaging_Kitchen.png')}
          alt='Most Popular Homemade Food in Locations'
        />
      </div>
      <div className='leading-trim-both text-edge-cap flex w-full flex-col justify-center gap-3 self-stretch text-base font-normal leading-[120%] text-eggplant md:w-1/2'>
        <span>
          Have you ever dreamt of recreating an authentic dish you love from another culture? Sourcing hard-to-find
          ingredients for the perfect Pad Thai, a fabulous Fatoush, or a mouthwatering Mole can be a challenge. Getting
          the flavors and traditional techniques right takes a lifetime of first-hand experience.
        </span>
        <span>
          Reconnecting with the globally inspired dishes you've been missing isn't just about finding ingredients and
          following recipes. It goes deeper than that. It's about tapping into culinary wisdom passed down through
          generations.
        </span>
        <span>
          Shef connects you to the international community of talented cooks in your area, bringing the dishes you crave
          right to your doorstep. From the furthest reaches of the globe to your kitchen table, there's a world of taste
          and tradition waiting to be discovered.
        </span>
        <span>Shef is flavor that takes you places.</span>
      </div>
    </div>
  </ContentContainer>
);

export const HEADER_COPY_TITLE_3 = 'Authentically Homemade Cooked Meals Delivered';
export const AUTHENTIC_BLURBS: BlurbShape[] = [
  {
    key: 'homemade',
    title: 'The Story of Homemade',
    // icon: "fal fa-house",
    imageUrl: getAssetPath('/icons/static/svg/home-static-icon.svg'),
    text: (
      <>
        <span>
          Not only is{' '}
          <Link
            className='text-raspberry hover:text-raspberry-hover'
            href='https://shef.com/homemade-food/the-story-of-homemade/'>
            homemade
          </Link>{' '}
          better for you, but we believe that it <i>tastes</i> better too. <br />
        </span>
        <span>
          Let's explore how homemade meals from our community of shefs compare to restaurant food in terms of health
          benefits, taste, and more.
        </span>
      </>
    ),
    buttonText: 'Homemade Food',
    buttonLink: 'https://shef.com/homemade-food/the-story-of-homemade/',
  },
  {
    key: 'safety',
    title: 'Food Safety',
    imageUrl: getAssetPath('/icons/static/svg/shield-static-icon.svg'),
    text: `Our community of shefs care deeply about the people they’re cooking for. 
    The majority of shefs are moms that have spent years cooking for their own families, 
    with their wellbeing front of mind. That being said, your trust is our #1 priority, 
    so we’ve implemented strict food safety protocols to give you extra peace of mind. 
    Read more about our food safety policy here.`,
    buttonText: 'Food Safety',
    buttonLink: 'https://shef.com/homemade-food/food-safety/',
  },
  {
    key: 'authentic',
    title: 'Why Authentic is Hard to Find!',
    imageUrl: getAssetPath('/icons/static/svg/cup-static-icon.svg'),
    text: `As a nation, the United States is a melting pot of global cultures. 
    So why is it that you find the same dishes in Italian restaurants and Indian 
    restaurants no matter where you are? While there are many reasons why 
    authentic food is hard to find, there are a few prevalent reasons. 
    Let’s explore them, shall we?`,
    buttonText: 'Explore Authentic Food',
    buttonLink: 'https://shef.com/homemade-food/why-is-authentic-food-so-hard-to-find-in-the-u-s/',
  },
];

export const HEADER_COPY_TITLE_4 = 'Our Shefs. Our Stories.';
export const STORY_BLURBS: BlurbShape[] = [
  {
    key: 'every',
    title: 'Every Shef Has a Story!',
    imageUrl: getAssetPath('/images/CDN/cutting_board.png'),
    imageAlt: 'Shef cooking in kitchen',
    text: `The beauty in a homemade meal is that it has the ability to tell a story. 
    When people cook with patience and passion, the end result is a dish that acts as a piece of artwork, 
    a beautiful reflection of the artist who created it. Food that's every bit as unique and interesting 
    as the story behind the shef that made it.`,
    buttonText: 'Read Shef Stories',
    buttonLink: 'https://shef.com/homemade-food/every-shef-has-a-story/',
  },
  {
    key: 'shef-story',
    title: 'The Story of Shef',
    imageUrl: getAssetPath('/images/CDN/family_table.png'),
    imageAlt: 'Shef company story',
    text: `At Shef, we're rebuilding the food system from scratch, redefining who can participate in the food economy, 
    and returning personal connection to the making, eating, and sharing of food.`,
    buttonText: 'Read Shef Stories',
    buttonLink: 'https://blog.shef.com/our-story',
  },
];
